import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  StatusBadge,
} from '@/components/ui'
import { deleteCargo, fetchCargos } from './api'
import { formatMoney } from './utils'

export function CargosPage() {
  const { canEdit } = useAuth()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('ativo')
  const [area, setArea] = useState('')
  const [comColab, setComColab] = useState(false)
  const [page, setPage] = useState(1)
  const [msg, setMsg] = useState('')

  const lista = useQuery({
    queryKey: ['rh', 'cargos', q, status, area, comColab, page],
    queryFn: () =>
      fetchCargos({
        busca: q || undefined,
        status: status || undefined,
        area: area || undefined,
        comColaboradores: comColab || undefined,
        page,
      }),
  })

  const remover = useMutation({
    mutationFn: (id: string) => deleteCargo(id),
    onSuccess: async () => {
      setMsg('Cargo removido.')
      await qc.invalidateQueries({ queryKey: ['rh', 'cargos'] })
    },
    onError: (err) => {
      setMsg(err instanceof ApiError ? err.message : 'Falha ao remover.')
    },
  })

  const dados = lista.data?.dados || []
  const meta = lista.data?.meta
  const areas = lista.data?.areas || []
  const total = meta?.total ?? dados.length

  return (
    <section className="hi-page hi-cargos-page">
      <PageHeader
        title="Cargos"
        subtitle="Catálogo com descritivo, competências, faixas e headcount."
        actions={
          <>
            <a className="btn btn-secondary" href={legacyAppUrl('#/cargos')}>
              Legado
            </a>
            {canEdit('cargos') ? (
              <>
                <Link className="btn btn-secondary" to="/app/cargos/importar">
                  Importar
                </Link>
                <Link className="btn btn-primary" to="/app/cargos/novo">
                  Novo cargo
                </Link>
              </>
            ) : null}
          </>
        }
      />

      {msg ? <Alert tone="info">{msg}</Alert> : null}

      <KpiGrid>
        <Kpi className="hi-cargos-kpi hi-cargos-kpi--list" label="Listados" value={String(total)} />
        <Kpi
          className="hi-cargos-kpi hi-cargos-kpi--areas"
          label="Áreas"
          value={String(areas.length)}
        />
      </KpiGrid>

      <section className="card hi-cargos-panel">
        <div className="card-header hi-cargos-panel-head">
          <div className="hi-cargos-panel-title">
            <h4 className="card-title">Cargos</h4>
            <Muted>
              {lista.isFetching ? 'Atualizando…' : `${total.toLocaleString('pt-BR')} no filtro`}
            </Muted>
          </div>
        </div>

        <form
          className="hi-filters hi-cargos-toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(search.trim())
          }}
        >
          <input
            type="search"
            placeholder="Nome, CBO, área…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar cargos"
          />
          <select
            value={status}
            onChange={(e) => {
              setPage(1)
              setStatus(e.target.value)
            }}
            aria-label="Status"
          >
            <option value="">Todos</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
          </select>
          <select
            value={area}
            onChange={(e) => {
              setPage(1)
              setArea(e.target.value)
            }}
            aria-label="Área"
          >
            <option value="">Todas as áreas</option>
            {areas.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <label className="hi-check" style={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={comColab}
              onChange={(e) => {
                setPage(1)
                setComColab(e.target.checked)
              }}
            />
            Com colaboradores
          </label>
          <button type="submit" className="btn btn-primary">
            Buscar
          </button>
        </form>

        <div className="hi-cargos-table">
          {lista.isLoading ? <Muted>Carregando…</Muted> : null}
          {lista.isError ? (
            <Alert tone="danger">
              {lista.error instanceof ApiError ? lista.error.message : 'Falha ao listar.'}
            </Alert>
          ) : null}
          {!lista.isLoading && dados.length === 0 ? <Empty>Nenhum cargo neste filtro.</Empty> : null}
          {dados.length > 0 ? (
            <DataTable>
              <thead>
                <tr>
                  <th scope="col">Cargo</th>
                  <th scope="col">Área / Depto</th>
                  <th scope="col">Nível</th>
                  <th scope="col" className="is-num">
                    Faixa
                  </th>
                  <th scope="col" className="is-num">
                    HC
                  </th>
                  <th scope="col">Status</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {dados.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link className="hi-cargos-nome" to={`/app/cargos/${c.id}`}>
                        {c.nome}
                      </Link>
                      {c.codigo_cbo ? <div className="hi-table-sub">CBO {c.codigo_cbo}</div> : null}
                    </td>
                    <td>
                      <span className="hi-cargos-area">{c.area || '—'}</span>
                      {c.departamento_nome ? (
                        <div className="hi-table-sub">{c.departamento_nome}</div>
                      ) : null}
                    </td>
                    <td>
                      {c.nivel_display || c.nivel || '—'}
                      {c.nivel_grade ? ` · G${c.nivel_grade}` : ''}
                    </td>
                    <td className="is-num">
                      {(() => {
                        const min = c.salario_min ?? c.salario_hc_min
                        const max = c.salario_max ?? c.salario_hc_max
                        const viaHc =
                          Boolean(c.salario_de_headcount) ||
                          (c.salario_min == null &&
                            c.salario_max == null &&
                            (c.salario_hc_min != null || c.salario_hc_max != null))
                        if (min == null && max == null) {
                          return (c.headcount ?? 0) > 0 ? (
                            <span title="Colaboradores sem salário_base preenchido">
                              <Muted>sem faixa</Muted>
                            </span>
                          ) : (
                            '—'
                          )
                        }
                        return (
                          <>
                            {formatMoney(min)} – {formatMoney(max)}
                            {viaHc ? <div className="hi-table-sub">via colaboradores</div> : null}
                          </>
                        )
                      })()}
                    </td>
                    <td className="is-num hi-cargos-hc">{c.headcount ?? 0}</td>
                    <td>
                      <StatusBadge tone={c.ativo === false ? 'inativo' : 'ativo'}>
                        {c.ativo === false ? 'Inativo' : 'Ativo'}
                      </StatusBadge>
                    </td>
                    <td className="hi-cargos-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/app/cargos/${c.id}`)}
                      >
                        {canEdit('cargos') ? 'Editar' : 'Ver'}
                      </button>
                      {canEdit('cargos') ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={remover.isPending}
                          onClick={() => {
                            if (!window.confirm(`Remover o cargo “${c.nome}”?`)) return
                            remover.mutate(c.id)
                          }}
                        >
                          Remover
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          ) : null}
        </div>
      </section>

      {meta && (meta.tem_anterior || meta.tem_proxima) ? (
        <div className="hi-pager hi-cargos-pager">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!meta.tem_anterior || lista.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <Muted>
            Página {meta.pagina ?? page}
            {meta.ultima_pagina ? ` de ${meta.ultima_pagina}` : ''}
          </Muted>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={!meta.tem_proxima || lista.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
          </button>
        </div>
      ) : null}
    </section>
  )
}

export default CargosPage
